// Guest (deferred) auth: MorrowOS lets signed-out users "chat first, sign in
// later" (like ChatGPT). A guest may send a limited number of chat messages
// before we prompt them to sign in with Clerk. We track the count client-side
// in localStorage so it survives reloads but stays trivially simple — this is a
// nudge, not a security boundary (the backend enforces real access separately).

const GUEST_MESSAGE_COUNT_KEY = "morrow.guestMessageCount";
// When the guest's current draft thread was first created (epoch ms). Used to
// expire abandoned guest drafts that were never claimed by signing in.
const GUEST_DRAFT_CREATED_AT_KEY = "morrow.guestDraftCreatedAt";

// How many messages a signed-out guest may send before we prompt sign-in.
export const GUEST_FREE_MESSAGE_LIMIT = 1;

// How long an unclaimed guest draft lives before it's discarded on the next app
// load. A guest who sent a message and then wandered off gets a clean slate (and
// a fresh free message) rather than being stuck behind a stale sign-in wall.
export const GUEST_DRAFT_TTL_MS = 24 * 60 * 60 * 1000; // 24h

function safeGetStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    // localStorage can throw in privacy modes / sandboxed frames.
    return null;
  }
}

/** How many chat messages this guest has sent so far (0 if unknown). */
export function getGuestMessageCount(): number {
  const storage = safeGetStorage();
  if (!storage) return 0;
  const raw = storage.getItem(GUEST_MESSAGE_COUNT_KEY);
  const parsed = raw == null ? 0 : Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/** Record that the guest sent one more message; returns the new total. */
export function incrementGuestMessageCount(): number {
  const next = getGuestMessageCount() + 1;
  const storage = safeGetStorage();
  if (storage) {
    try {
      storage.setItem(GUEST_MESSAGE_COUNT_KEY, String(next));
      // Stamp the guest draft's birth on the first message so we can expire it
      // later if it's never claimed by signing in.
      if (next === 1 && storage.getItem(GUEST_DRAFT_CREATED_AT_KEY) == null) {
        storage.setItem(GUEST_DRAFT_CREATED_AT_KEY, String(Date.now()));
      }
    } catch {
      // Ignore write failures — the counter is best-effort.
    }
  }
  return next;
}

/**
 * Whether a signed-out guest has already used up their free message(s) and so
 * should be prompted to sign in before sending again.
 */
export function guestHasExhaustedFreeMessages(): boolean {
  return getGuestMessageCount() >= GUEST_FREE_MESSAGE_LIMIT;
}

// Connect-app gating (implemented at the UI layer): connecting an app is a
// persisted, per-user integration, so a signed-out guest can never do it —
// unlike the free-message allowance there is NO free tier. The connect entry
// points (ChatView's landing cards, ConnectionsSettings' catalog grid) gate on
// `isClerkLoaded && !isSignedIn`, showing a "Sign in to connect apps" prompt and
// calling clerk.openSignIn() instead of starting the Composio OAuth flow.

/**
 * Clear the guest free-message state — call this once a user has signed in so
 * their (now claimed) chat is never gated again. The guest thread/draft itself
 * is intentionally left in place: signing in continues the SAME chat.
 */
export function resetGuestMessageCount(): void {
  const storage = safeGetStorage();
  if (!storage) return;
  try {
    storage.removeItem(GUEST_MESSAGE_COUNT_KEY);
    storage.removeItem(GUEST_DRAFT_CREATED_AT_KEY);
  } catch {
    // Ignore.
  }
}

/**
 * On app load, discard the guest's free-message state if their draft thread is
 * older than {@link GUEST_DRAFT_TTL_MS} and was never claimed by signing in.
 * Returns true if a stale draft was pruned. Simple + local: it only clears the
 * guest counter/timestamp (so the guest gets a fresh free message); it never
 * touches a thread the user is actively in — callers gate this on `!isSignedIn`.
 */
export function pruneExpiredGuestDraft(now: number = Date.now()): boolean {
  const storage = safeGetStorage();
  if (!storage) return false;
  const raw = storage.getItem(GUEST_DRAFT_CREATED_AT_KEY);
  if (raw == null) return false;
  const createdAt = Number.parseInt(raw, 10);
  if (!Number.isFinite(createdAt)) {
    // Corrupt value — clear it so we don't re-evaluate a bad stamp forever.
    resetGuestMessageCount();
    return true;
  }
  if (now - createdAt < GUEST_DRAFT_TTL_MS) return false;
  resetGuestMessageCount();
  return true;
}
